import { Host, h } from "@stencil/core";
import { markdownToJSX } from "./parsers/markdown-to-jsx";
import { defaultCodeRender } from "./parsers/code-highlight";
/**
 * @deprecated Use the `ch-markdown-viewer` component instead.
 *
 * A control to render markdown syntax. It supports GitHub Flavored Markdown
 * (GFM) and code highlighting.
 *  - It parses the incoming markdown to [mdast](https://github.com/syntax-tree/mdast) using [micromark](https://github.com/micromark/micromark) via [mdast-util-from-markdown](https://github.com/syntax-tree/mdast-util-from-markdown).
 *
 * - After that, it implements a reactivity layer by implementing its own render for the mdast. With this, changes to the input markdown only update the portion of the DOM that changes.
 *
 * - It supports Github Flavored Markdown (GFM) by using [mdast-util-gfm](https://github.com/syntax-tree/mdast-util-gfm) and [micromark-extension-gfm](https://github.com/micromark/micromark-extension-gfm).
 *
 * - It supports code highlight by parsing the incomming code of the markdown to [hast](https://github.com/micromark/micromark-extension-gfm) using [lowlight](lowlight). After that, it implements a reactivity layer by implementing its own render for the hast.
 *
 * - It also supports all programming languages from [highlight.js](https://github.com/highlightjs/highlight.js).
 *
 * - When the code highlighting is needed at runtime, the control will load on demand the code parser and the programming language needed to parse the code.
 */
export class ChMarkdown {
    constructor() {
        this.rawHtml = false;
        this.renderCode = defaultCodeRender;
        this.value = undefined;
    }
    #JSXTree;
    el;
    async componentWillRender() {
        if (!this.value) {
            return;
        }
        this.#JSXTree = await markdownToJSX(this.value, {
            rawHTML: this.rawHtml,
            allowDangerousHtml: true, // Allow dangerous in this version
            renderCode: this.renderCode
        });
    }
    render() {
        if (!this.value) {
            return "";
        }
        return h(Host, null, this.#JSXTree);
    }
    static get is() { return "ch-markdown"; }
    static get originalStyleUrls() {
        return {
            "$": ["markdown.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["markdown.css"]
        };
    }
    static get properties() {
        return {
            "rawHtml": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` to render raw HTML with sanitization."
                },
                "attribute": "raw-html",
                "reflect": false,
                "defaultValue": "false"
            },
            "renderCode": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "MarkdownCodeRender",
                    "resolved": "(options: MarkdownCodeRenderOptions) => any",
                    "references": {
                        "MarkdownCodeRender": {
                            "location": "import",
                            "path": "./parsers/types",
                            "id": "src/deprecated-components/markdown/parsers/types.ts::MarkdownCodeRender"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This property allows us to implement custom rendering for the code blocks."
                },
                "defaultValue": "defaultCodeRender"
            },
            "value": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the markdown string to parse."
                },
                "attribute": "value",
                "reflect": false
            }
        };
    }
    static get elementRef() { return "el"; }
}
